import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { usePubNub } from "pubnub-react";
import { useEffect, useMemo } from "react";
import {
    AI_GENERATION_COMPLETION_STATUS,
    ALLOWED_PUBUNB_MESSAGE_TYPE,
    PUBNUB_MESSAGE_ACTION,
    PUBNUB_MESSAGE_TYPE
} from "../../constants";

import {
    aiTemplateGenerationAtom,
    setAiTemplateGenerationCompletionStatus
} from "../../stores/jotai/generation-progress/ai/aiTemplateGeneration";
import { baseAtom, downloadExportInterviewSignedURL, getChannels, recieveLinxPayload } from "../../stores/jotai/base";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { aiInsightGenerationProgressAtom, setAiInsightGenerationProgress } from "../../stores/jotai/insights/aiInsightGenerationStatus";
import { migrationStateAtom, setIsTranscriptMigrated } from "../../stores/jotai/migration/migration";

import { scoringSelectedPeriodAtom } from "../../stores/jotai/scoring/selectedPeriodIndex";
import { setIsTranscriptLoadedFromPubnub, transcriptStateAtom } from "../../stores/jotai/transcript/transcript";
import { importCanvasAtom, setimportCanvasCompletionStatus } from "../../stores/jotai/generation-progress/import/importCanvasProcess";
import { aiCanvasGenerationAtom, setAiCanvasGenerationCompletionStatus, } from "../../stores/jotai/generation-progress/ai/aiCanvasGeneration";
import { reTranscriptionProgressAtom, setRetrancriptionProgressByInterviewID } from "../../stores/jotai/transcript/reTranscriptionProgress";
import { exportInterviewAtom, setExportInterviewCompletionStatus } from "../../stores/jotai/generation-progress/export/exportInterviewProcess";
import { TUserAuthStatusResponse } from "../../tanstackQuery/social-integrations/types";
import { zoomIntegrationStateAtom } from "../../stores/jotai/calender&Zoom";

const PubnubListener = () => {
    const [base, setBase] = useAtom(baseAtom);
    const [
        transcriptAtom,
        setTranscriptAtom,
    ] = useAtom(transcriptStateAtom);
    const [migrationState, setMigrationState] = useAtom(migrationStateAtom);
    const discoverySession = useAtomValue(discoverySessionAtom);
    const setAiCanvaseGenerationState = useSetAtom(aiCanvasGenerationAtom);
    const setAiTemplateGenerationAtom = useSetAtom(aiTemplateGenerationAtom);
    const setAiInsightGenerationProgressAtom = useSetAtom(
        aiInsightGenerationProgressAtom
    );
    const setExportInterviewState=useSetAtom(exportInterviewAtom);
    const setImportCanvasState=useSetAtom(importCanvasAtom);
    const setRetranscriptionProgress = useSetAtom(reTranscriptionProgressAtom);
    const selectedPeriodIndex = useAtomValue(scoringSelectedPeriodAtom);
    const pubnub = useMemo(() => usePubNub(), []);
    const teamID = base?.team?.groupID;
    const userID = base?.user?.userID;
    const [zoomIntegration, setZoomIntegration] = useAtom(
      zoomIntegrationStateAtom
    );

    const client = useQueryClient();
      const authStatus = client.getQueryData([
        "user-auth-status",
        { userId: base?.user?.userID, teamId: base?.team?.id }
      ]) as TUserAuthStatusResponse;

    const instructorChannels = useMemo(() => {
        return [
            {
                key: `${teamID}P${selectedPeriodIndex}`,
                value: "Presentation Chat",
            },
        ];
    }, [selectedPeriodIndex]);

    const generalChannels = useMemo(() => {
        return getChannels(base);
    }, [!!base.user, !!base.team, !!base.cohort, base.roleSnapshot]);

    const handleAiCanvaGenerationMessages = (pubnubMessage) => {
        const pubnubMessageType = pubnubMessage?.type;
        const pubnubMessageAction = pubnubMessage?.action;
        const pubnubMessageMessage = pubnubMessage?.message;
        const teamIDFromPubnub = pubnubMessageMessage?.groupID;

        if (pubnubMessageType === PUBNUB_MESSAGE_TYPE.AI_CANVAS) {
            if (pubnubMessageAction === PUBNUB_MESSAGE_ACTION.GENERATE) {
                if (pubnubMessageMessage?.success) {
                    setAiCanvasGenerationCompletionStatus(
                        setAiCanvaseGenerationState,
                        teamIDFromPubnub,
                        AI_GENERATION_COMPLETION_STATUS.SUCCESSFULL
                    );
                } else {
                    setAiCanvasGenerationCompletionStatus(
                        setAiCanvaseGenerationState,
                        teamIDFromPubnub,
                        AI_GENERATION_COMPLETION_STATUS.FAILED
                    );
                }
            }

            client.invalidateQueries({ queryKey: ["canvas"] });
        } 
    };

    const handleExportInterviewMessages = (pubnubMessage) => {
        const pubnubMessageType = pubnubMessage?.type;
        const pubnubMessageMessage = pubnubMessage?.message;
        const teamIDFromPubnub = pubnubMessageMessage?.groupID;

        if (pubnubMessageType===PUBNUB_MESSAGE_TYPE.EXPORT_TEAM_INTERVIEW) {
            if (pubnubMessageMessage?.success) {
                setExportInterviewCompletionStatus(
                    setExportInterviewState,
                    teamIDFromPubnub,
                    AI_GENERATION_COMPLETION_STATUS.SUCCESSFULL
                );

                const signedUrl = pubnubMessageMessage?.signedUrl;
                downloadExportInterviewSignedURL(signedUrl,base?.team?.name);

            } else {
                setExportInterviewCompletionStatus(
                    setExportInterviewState,
                    teamIDFromPubnub,
                    AI_GENERATION_COMPLETION_STATUS.FAILED
                );
            }
        } 
    };
    const handleImportCanvasMessages = (pubnubMessage) => {
        const pubnubMessageType = pubnubMessage?.type;
        const pubnubMessageMessage = pubnubMessage?.message;
        const teamIDFromPubnub = pubnubMessageMessage?.groupID;

        if (pubnubMessageType===PUBNUB_MESSAGE_TYPE.CANVAS_IMPORT) {
            if (pubnubMessageMessage?.success) {
                setimportCanvasCompletionStatus(
                    setImportCanvasState,
                    teamIDFromPubnub,
                    AI_GENERATION_COMPLETION_STATUS.SUCCESSFULL
                );

            } else {
                setimportCanvasCompletionStatus(
                    setImportCanvasState,
                    teamIDFromPubnub,
                    AI_GENERATION_COMPLETION_STATUS.FAILED
                );
            }
        } 
    };

    const handleAiTemplateGenerationMessages = (pubnubMessage) => {
        const pubnubMessageType = pubnubMessage?.type;
        const pubnubMessageAction = pubnubMessage?.action;
        const pubnubMessageMessage = pubnubMessage?.message;
        const teamIDFromPubnub = pubnubMessageMessage?.groupID;

        if (pubnubMessageType === PUBNUB_MESSAGE_TYPE.AI_TEMPLATE) {
            if (pubnubMessageAction === PUBNUB_MESSAGE_ACTION.GENERATE) {
                if (pubnubMessageMessage?.success) {
                    setAiTemplateGenerationCompletionStatus(
                        setAiTemplateGenerationAtom,
                        teamIDFromPubnub,
                        AI_GENERATION_COMPLETION_STATUS.SUCCESSFULL
                    );
                } else {
                    setAiTemplateGenerationCompletionStatus(
                        setAiTemplateGenerationAtom,
                        teamIDFromPubnub,
                        AI_GENERATION_COMPLETION_STATUS.FAILED
                    );
                }
            }
        } 
    };

    const handleAiInsightGenerationMessages = (pubnubMessage) => {
        const pubnubMessageType = pubnubMessage?.type;
        const pubnubMessageAction = pubnubMessage?.action;
        const pubnubMessagePayload = pubnubMessage?.message;

        if (pubnubMessageType === PUBNUB_MESSAGE_TYPE.AI_INSIGHT) {
            if (pubnubMessageAction === PUBNUB_MESSAGE_ACTION.GENERATE) {
                const interviewID = pubnubMessagePayload?.interviewID;;
                const progressStatus = ["in_progress","initiate"].includes(pubnubMessagePayload?.status)  ? "in_progress" : pubnubMessagePayload?.status;
                const artifactType = pubnubMessagePayload?.artifactType;

                if (
                    !!progressStatus &&
                    !!interviewID
                ) {
                    setAiInsightGenerationProgress(
                        setAiInsightGenerationProgressAtom,
                        artifactType,
                        interviewID,
                        progressStatus,
                        pubnubMessagePayload?.insightCount
                    );
                    client.invalidateQueries({ queryKey: ["insights"] })
                    client.invalidateQueries({queryKey: ["finding-links"]})
                }
            }
        } 
    };

    const updateTranscriptLoadedFromPubnub = (pubnubMessage) => {
        const pubnubMessageType = pubnubMessage?.type;

        if(pubnubMessageType === PUBNUB_MESSAGE_TYPE.DEEPGRAM && pubnubMessage?.message?.attachedTranscript) {
            setIsTranscriptLoadedFromPubnub(
                setTranscriptAtom,
                true
            );
        }
    }

    const handleTranscriptMigration = (pubnubMessage) => {
        const pubnubMessageType = pubnubMessage?.type;

        if (pubnubMessageType === PUBNUB_MESSAGE_TYPE.MEDIA_ASSEMBLY) {
            setIsTranscriptMigrated(setMigrationState, true);
        }
    };

    const updateReTranscriptionProgress = (pubnubMessage) => {
        const pubnubMessagePayload = pubnubMessage?.message;
        const interviewID = pubnubMessagePayload?.interviewID;

        setRetrancriptionProgressByInterviewID(
            setRetranscriptionProgress,
            interviewID,
            false,
        );
    };

    const handleMessage = (event) => {
        try {
            const pubnubMessage = event.message;
            const pubnubMessageType = pubnubMessage?.type;
            const pubnubMessageAction = pubnubMessage?.action;

            if (pubnubMessage?.source === discoverySession.sessionID) {
                if(pubnubMessageType===PUBNUB_MESSAGE_TYPE.AI_TEMPLATE){
                    handleAiTemplateGenerationMessages(pubnubMessage)
                }
                
                if(pubnubMessageType===PUBNUB_MESSAGE_TYPE.AI_CANVAS){
                    handleAiCanvaGenerationMessages(pubnubMessage);
                }

                if(pubnubMessageType===PUBNUB_MESSAGE_TYPE.EXPORT_TEAM_INTERVIEW){
                    handleExportInterviewMessages(pubnubMessage);
                }

                if(pubnubMessageType===PUBNUB_MESSAGE_TYPE.CANVAS_IMPORT){
                    handleImportCanvasMessages(pubnubMessage);
                }
            }

            if (
              pubnubMessageAction === "delete" &&
              (authStatus?.zoomUserUUID ===
                pubnubMessage?.message?.zoomUserUUID ||
                authStatus?.googleUserUUID ===
                  pubnubMessage?.message?.googleUserUUID)
            ) {
              client.invalidateQueries({ queryKey: ["user-auth-status"] });
            }

            if (
              pubnubMessageType === "meeting" &&
              pubnubMessageAction === "state_change"
            ) {
              client.invalidateQueries({ queryKey: ["zoom-meetings"] });
              setZoomIntegration((prev) => ({ ...prev, eventsRefetching: true, eventsRefetchingWithPubnub: true }));
            }

            if (pubnubMessage?.message?.user?.userID === base?.user?.userID) {
                handleAiInsightGenerationMessages(pubnubMessage);
            }

            if (pubnubMessage?.message?.user?.userID === base?.user?.userID) {
                if(pubnubMessageType === PUBNUB_MESSAGE_TYPE.AI_INSIGHT){
                    handleAiInsightGenerationMessages(pubnubMessage);
                }
            }

            if (pubnubMessageType === PUBNUB_MESSAGE_TYPE.INTERVIEW) {
                client.invalidateQueries({ queryKey: ["interviews"] });
                client.invalidateQueries({ queryKey: ["artifacts"] });
            }

            if (pubnubMessageType === PUBNUB_MESSAGE_TYPE.DEEPGRAM) {
                updateReTranscriptionProgress(pubnubMessage);
                client.invalidateQueries({ queryKey: ["interviews"] });
            }

            if (pubnubMessageType === PUBNUB_MESSAGE_TYPE.ACTIVITY) {
                if (
                    pubnubMessageAction === PUBNUB_MESSAGE_ACTION.BULK_CREATE
                ) {
                    client.invalidateQueries({ queryKey: ["activities"] });
                }
            }

            if (ALLOWED_PUBUNB_MESSAGE_TYPE.includes(pubnubMessageType)) {
                recieveLinxPayload(setBase, pubnubMessage, base?.team?.isAiInsightGenerationEnabled, client);
            }

            updateTranscriptLoadedFromPubnub(pubnubMessage);
            handleTranscriptMigration(pubnubMessage);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        const listenerParams = { message: handleMessage };
        const channels: string[] = [];
        const teamChannel = `${teamID}-linx`;
        const userChannel = `${userID}-linx`;

        if (!!teamID) {
            channels.push(
                teamChannel,
                ...instructorChannels.map((ch) => `${ch.key}`)
            );
        }

        if (!!userID) {
            channels.push(userChannel);
        }

        if (generalChannels.length > 0) {
            channels.push(...generalChannels.map((ch) => `${ch.key}`));
        }

        pubnub.addListener(listenerParams);
        pubnub.subscribe({
            channels,
        });

        return () => {
            pubnub.unsubscribe({ channels });
            pubnub.removeListener(listenerParams);
        };
    }, [
        pubnub,
        teamID,
        userID,
        instructorChannels,
        generalChannels,
    ]);

    return null;
};

export default PubnubListener;
